import { NotifyHelper } from 'classes/helpers/notify.helper';
import { t } from 'i18next';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { AdminMachinesRoutes } from 'lib_ts/enums/routing/routes/admin-machines';
import {
  IError,
  IErrorsQuery,
  IErrorsWithTotal,
} from 'lib_ts/interfaces/common/i-error';
import { IErrorType } from 'lib_ts/interfaces/common/i-error-type';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IPaginatedShotHistory } from 'lib_ts/interfaces/data/i-machine-shot-history';
import {
  IPitchPerfFilters,
  IPitchPerfResults,
} from 'lib_ts/interfaces/data/i-pitch-performance';
import { IAdminMachine, IMachine } from 'lib_ts/interfaces/i-machine';
import { IMachineConfig } from 'lib_ts/interfaces/i-machine-config';
import {
  IMachineLogFilesFilters,
  IMachineLogFilesWithTotal,
} from 'lib_ts/interfaces/i-machine-log-file';
import { IMachineNetworkLog } from 'lib_ts/interfaces/i-machine-network-logs';
import { IWSMsg } from 'lib_ts/interfaces/i-machine-msg';
import { ICustomRequestMsg } from 'lib_ts/interfaces/machine-msg/i-custom-request';
import { IFirmwareUpgradeRequest } from 'lib_ts/interfaces/machine-msg/i-firmware-upgrade';
import { IFirmwareWriteMsg } from 'lib_ts/interfaces/machine-msg/i-firmware-write';
import { IGatherShotDataQuery } from 'lib_ts/interfaces/modelling/i-gather-shot-data';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import {
  IRepeatabilityFilter,
  IRepeatabilityResult,
} from 'lib_ts/interfaces/modelling/i-repeatability';
import { BaseRESTService } from 'services/_base-rest.service';

export interface IRecentSession {
  _id: string;
  total: number;
  max_created: string;
}

export class AdminMachinesService extends BaseRESTService {
  private static instance: AdminMachinesService;
  static getInstance(): AdminMachinesService {
    if (!AdminMachinesService.instance) {
      AdminMachinesService.instance = new AdminMachinesService();
    }

    return AdminMachinesService.instance;
  }

  private constructor() {
    super({
      controller: Controller.AdminMachines,
    });
  }

  /** get all machines, limited to user's team if not a super admin */
  async getMachines(global: boolean): Promise<IAdminMachine[]> {
    try {
      return await this.get({
        uri: AdminMachinesRoutes.GetMachines,
        params: { global: global } as any,
      });
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async getDetails(machineID: string): Promise<IAdminMachine | undefined> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetDetails}/${machineID}`,
      });
    } catch (e) {
      console.error(e);
    }
  }

  // i.e. models that are not archived
  async getActiveModels(): Promise<IMachineModel[]> {
    try {
      return await this.get({
        uri: AdminMachinesRoutes.GetActiveModels,
      });
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async upgradeFirmware(payload: IFirmwareUpgradeRequest): Promise<void> {
    try {
      return await this.post(
        {
          uri: AdminMachinesRoutes.UpgradeFirmware,
        },
        payload
      );
    } catch (e) {
      console.error(e);
    }
  }

  async checkFirmware(machineID: string): Promise<void> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.CheckFirmware}/${machineID}`,
      });
    } catch (e) {
      console.error(e);
    }
  }

  async getRecentSessions(machineID: string): Promise<IRecentSession[]> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetRecentSessions}/${machineID}`,
      }).then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              `There was an error getting recent sessions for ${machineID}.`,
          });
          return [];
        }

        return result.data;
      });
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async getPitchPerformance(
    machineID: string,
    filters: IPitchPerfFilters
  ): Promise<IPitchPerfResults | undefined> {
    try {
      return await this.post(
        {
          uri: `${AdminMachinesRoutes.GetPitchPerformance}/${machineID}`,
        },
        filters
      )
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                `There was an error getting pitch performance for ${machineID}.`,
            });
            return undefined;
          }

          return result.data;
        })
        .catch((e) => {
          console.error(e);
          return undefined;
        });
    } catch (e) {
      console.error(e);
    }
  }

  async getConfig(machineID: string): Promise<IMachineConfig | undefined> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetConfig}/${machineID}`,
      })
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                `There was an error getting configuration for ${machineID}.`,
            });
            return undefined;
          }

          if (!result.data) {
            NotifyHelper.warning({
              message_md: `The last config found for ${machineID} is empty.`,
            });
            return undefined;
          }

          return result.data;
        })
        .catch((error) => {
          console.error(error);
          NotifyHelper.error({
            message_md: `There was an error getting configuration for ${machineID}.`,
          });
          return undefined;
        });
    } catch (e) {
      console.error(e);
    }
  }

  async setMachineConfig(
    machineID: string,
    msg: Partial<IFirmwareWriteMsg>
  ): Promise<boolean> {
    try {
      return await this.put(
        {
          uri: `${AdminMachinesRoutes.UpdateConfig}/${machineID}`,
        },
        msg
      )
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                `There was an error while sending configuration update request to ${machineID}.`,
            });
            return false;
          }

          NotifyHelper.info({
            message_md: `Sent request to update configuration to ${machineID}. Please wait...`,
          });
          return true;
        })
        .catch((error) => {
          console.error(error);
          NotifyHelper.error({
            message_md: `There was an error while sending configuration update request to ${machineID}.`,
          });
          return false;
        });
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async sideloadWebSocket(
    machineID: string,
    type: WsMsgType,
    payload: any
  ): Promise<void> {
    try {
      return await this.post(
        {
          uri: `${AdminMachinesRoutes.SideloadWebSocket}/${machineID}/${type}`,
        },
        payload
      );
    } catch (e) {
      console.error(e);
    }
  }

  async postCustomRequest(
    machineID: string,
    data: ICustomRequestMsg
  ): Promise<boolean> {
    try {
      const msg: IWSMsg = {
        type: WsMsgType.Misc_CustomRequest,
        machineID: machineID,
        data: data,
      };

      return await this.post(
        {
          uri: `${AdminMachinesRoutes.PostCustomRequest}/${machineID}`,
        },
        msg
      )
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                `There was an error while sending custom request to ${machineID}.`,
            });
            return false;
          }

          NotifyHelper.info({
            message_md: `Sent custom request to ${machineID}.`,
          });
          return true;
        })
        .catch((error) => {
          console.error(error);
          NotifyHelper.error({
            message_md: `There was an error while sending custom request to ${machineID}.`,
          });
          return false;
        });
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  /** for admins, triggers clearing of all connections to a specific machine */
  async resetConnections(machineID: string): Promise<void> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.ResetConnections}/${machineID}`,
      });
    } catch (e) {
      console.error(e);
    }
  }

  async create(data: Partial<IMachine>): Promise<IAdminMachine | undefined> {
    try {
      if (!data.machineID) {
        throw new Error('Cannot post without machineID');
      }

      if (!data._parent_id || !data._parent_def || !data._parent_field) {
        throw new Error('Cannot post without parent attributes');
      }

      return this.post(
        {
          uri: `${AdminMachinesRoutes.CreateForTeam}/${data._parent_id}`,
        },
        data
      );
    } catch (e) {
      console.error(e);
    }
  }

  async updateMachine(
    data: Partial<IMachine>
  ): Promise<IAdminMachine | undefined> {
    try {
      if (!data._id) {
        NotifyHelper.warning({
          message_md: 'Cannot update machine without ID',
        });
        return;
      }

      return this.put(
        {
          uri: AdminMachinesRoutes.UpdateMachine,
          params: {
            machine_id: data._id,
          } as any,
        },
        data
      )
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md: result.error ?? t('common.request-failed-msg'),
            });
            return undefined;
          }

          return result.data as IAdminMachine;
        })
        .catch((reason) => {
          console.error(reason);
          NotifyHelper.error({
            message_md: t('common.request-failed-msg'),
          });
          return undefined;
        });
    } catch (e) {
      console.error(e);
    }
  }

  async getErrors(payload: IErrorsQuery): Promise<IErrorsWithTotal> {
    try {
      return await this.post(
        {
          uri: `${AdminMachinesRoutes.GetErrors}/${payload.machineID}`,
        },
        payload
      )
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                'There was an error while fetching machine errors.',
            });
            return {
              total: 0,
              data: [],
            };
          }

          return result.data as IErrorsWithTotal;
        })
        .catch((reason) => {
          console.error(reason);
          NotifyHelper.error({
            message_md: 'There was an error while fetching machine errors.',
          });
          return {
            total: 0,
            data: [],
          };
        });
    } catch (e) {
      console.error(e);
      return {
        total: 0,
        data: [],
      };
    }
  }

  // from errors table, get all error-types that this machine has ever spawned
  async getMachineErrorTypes(machineID: string): Promise<IErrorType[]> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetMachineErrorTypes}/${machineID}`,
      })
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                'There was an error while fetching machine error types.',
            });
            return [];
          }

          return result.data;
        })
        .catch((reason) => {
          console.error(reason);
          NotifyHelper.error({
            message_md:
              'There was an error while fetching machine error types.',
          });
          return [];
        });
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  // for the AP > machine > status > network speed section
  async getLatestNetworkLog(
    machineID: string
  ): Promise<IMachineNetworkLog | undefined> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetLatestNetworkLog}/${machineID}`,
      })
        .then((result: IServerResponse) => {
          if (!result.success) {
            return undefined;
          }

          return result.data as IMachineNetworkLog | undefined;
        })
        .catch((reason) => {
          console.error(reason);
          return undefined;
        });
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  // i.e. error's _id > error's errorID > error-type by errorID
  async getMachineErrorWithType(error_id: string): Promise<
    | {
        error: IError;
        type: IErrorType | undefined;
      }
    | undefined
  > {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetMachineErrorWithType}/${error_id}`,
      })
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                'There was an error while fetching machine error + type by error ID.',
            });
            return undefined;
          }

          return result.data;
        })
        .catch((reason) => {
          console.error(reason);
          NotifyHelper.error({
            message_md:
              'There was an error while fetching machine error + type by error ID.',
          });
          return undefined;
        });
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  // todo: convert this to use a POST like getErrors above
  async getLogs(config: {
    machineID: string;
    skip: number;
    limit: number;
    filters?: IMachineLogFilesFilters;
  }): Promise<IMachineLogFilesWithTotal> {
    try {
      return await this.get({
        uri: `${AdminMachinesRoutes.GetLogs}/${config.machineID}`,
        params: {
          skip: config.skip,
          limit: config.limit,
          filters: config.filters,
        } as any,
      })
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.warning({
              message_md:
                result.error ??
                'There was an error while fetching machine logs.',
            });
            return {
              total: 0,
              data: [],
            };
          }

          return result.data as IMachineLogFilesWithTotal;
        })
        .catch((reason) => {
          console.error(reason);
          NotifyHelper.error({
            message_md: 'There was an error while fetching machine logs.',
          });
          return {
            total: 0,
            data: [],
          };
        });
    } catch (e) {
      console.error(e);
      return {
        total: 0,
        data: [],
      };
    }
  }

  async getShotHistory(
    payload: IGatherShotDataQuery
  ): Promise<IPaginatedShotHistory> {
    return await this.post(
      {
        uri: `${AdminMachinesRoutes.GetShotHistory}/${payload.machineID}`,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while fetching machine shot history.',
          });
          return {
            data: {
              shots: [],
              pitchDict: {},
              hitterDict: {},
            },
            has_more: false,
          };
        }

        return result.data as IPaginatedShotHistory;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md: 'There was an error while fetching machine shot history.',
        });
        return {
          data: {
            shots: [],
            pitchDict: {},
            hitterDict: {},
          },
          has_more: false,
        };
      });
  }

  async getRepeatabilityImages(
    filter: IRepeatabilityFilter
  ): Promise<IRepeatabilityResult> {
    try {
      return await this.post(
        {
          uri: AdminMachinesRoutes.GetRepeatabilityImages,
        },
        filter
      )
        .then((result: IServerResponse) => {
          if (!result.success) {
            NotifyHelper.error({
              message_md:
                result.error ??
                'There was an error while fetching repeatability report.',
            });
            return {
              success: false,
              images: [],
              summaries: [],
              error: result.error ?? 'Unknown error',
            };
          }
          return result.data as IRepeatabilityResult;
        })
        .catch((reason) => {
          console.error(reason);
          NotifyHelper.error({
            message_md:
              'There was an error while fetching repeatability report.',
          });

          return {
            success: false,
            images: [],
            summaries: [],
            error: reason,
          };
        });
    } catch (e) {
      console.error(e);
      return {
        success: false,
        images: [],
        summaries: [],
        error: 'There was an error while fetching repeatability report.',
      };
    }
  }

  async calibrateVentionPosition(payload: {
    machineID: string;
  }): Promise<boolean> {
    return await this.post(
      {
        uri: `${AdminMachinesRoutes.CalibrateVentionPosition}/${payload.machineID}`,
      },
      {}
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while sending calibrate vention position command.',
          });
          return false;
        }

        return true;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md:
            'There was an error while sending calibrate vention position command.',
        });
        return false;
      });
  }

  async calibrateAmpPosition(payload: { machineID: string }): Promise<boolean> {
    return await this.post(
      {
        uri: `${AdminMachinesRoutes.CalibrateAmpPosition}/${payload.machineID}`,
      },
      {}
    )
      .then((result: IServerResponse) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              'There was an error while sending calibrate amp position command.',
          });
          return false;
        }

        return true;
      })
      .catch((reason) => {
        console.error(reason);
        NotifyHelper.error({
          message_md:
            'There was an error while sending calibrate amp position command.',
        });
        return false;
      });
  }
}
